// `scriptctl review`: one process reviews a whole script against its original
// video. The agent itself lives in @lingjingai/review-agent and is loaded by a
// dynamic import so that every other command keeps its ~0.09s start-up.
//
// Progress is NDJSON on stdout, one line per episode as it finishes, because
// the caller persists per-episode results and must not lose them if the run
// is interrupted.
import * as fs from "node:fs";
import * as path from "node:path";
import { fileURLToPath, pathToFileURL } from "node:url";

import { CliError, EXIT_INPUT, EXIT_OK, EXIT_RUNTIME, type CommandOutput } from "../../common.js";
import { scriptJsonPath } from "../../common.js";
import { loadLlmConfig, type ResolvedLlmConfig } from "../../llm/config.js";
import { type Dict, strOf } from "../../domain/script/shared.js";

interface Profile {
  provider: string;
  model: string;
  baseUrl: string | null;
  apiKey: string;
}

const DEFAULT_PROFILE = "review-agent";

function resolveProfile(llm: ResolvedLlmConfig, override: string): Profile {
  const name = override || DEFAULT_PROFILE;
  const model = llm.models[name];
  if (!model) {
    throw new CliError(`LLM profile "${name}" is not defined.`, {
      exitCode: EXIT_INPUT,
      errorCode: "REVIEW_PROFILE_UNKNOWN",
      hint: `Define it under models, or pick one of: ${Object.keys(llm.models).join(", ")}.`,
      data: { expected: ["a defined models entry"], actual: [name] },
    });
  }
  const provider = llm.providers[model.provider];
  if (!provider) {
    throw new CliError(`Provider "${model.provider}" is not defined.`, {
      exitCode: EXIT_INPUT,
      errorCode: "REVIEW_PROVIDER_UNKNOWN",
      hint: `Available providers: ${Object.keys(llm.providers).join(", ")}.`,
      data: { expected: ["a defined providers entry"], actual: [model.provider] },
    });
  }
  const apiKey = (process.env[provider.apiKeyEnv] ?? "").trim();
  if (!apiKey) {
    throw new CliError(`${provider.apiKeyEnv} is not set.`, {
      exitCode: EXIT_INPUT,
      errorCode: "REVIEW_API_KEY_MISSING",
      hint: `Export ${provider.apiKeyEnv} for provider ${model.provider}.`,
      data: { expected: [provider.apiKeyEnv], actual: ["<empty>"] },
    });
  }
  return { provider: model.provider, model: model.model, baseUrl: provider.baseUrl, apiKey };
}

/**
 * The agent bundle sits next to this file in dist. It is loaded by path rather
 * than by package name so that a published scriptctl needs no resolvable
 * `@lingjingai/review-agent` — the bundle IS the package.
 */
async function loadAgent(): Promise<typeof import("@lingjingai/review-agent")> {
  const here = path.dirname(fileURLToPath(import.meta.url));
  // In the shipped bundle this module is dist/bin.js, so the sibling is
  // dist/review-agent.js; under tsc output it is dist/usecases/script/, so the
  // package name resolves through node_modules instead.
  const bundled = path.join(here, "review-agent.js");
  const specifier = fs.existsSync(bundled) ? pathToFileURL(bundled).href : "@lingjingai/review-agent";
  try {
    return await import(specifier) as typeof import("@lingjingai/review-agent");
  } catch (error) {
    throw new CliError("The review agent is not installed.", {
      exitCode: EXIT_RUNTIME,
      errorCode: "REVIEW_AGENT_MISSING",
      hint: "Reinstall scriptctl without --omit=optional, or install @mastra/core and @ai-sdk/google.",
      data: { expected: ["@lingjingai/review-agent"], actual: [String((error as Error).message).slice(0, 200)] },
    });
  }
}

function episodeList(raw: string): number[] | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const numbers = trimmed.split(",").map((part) => Number(part.trim()));
  if (numbers.some((value) => !Number.isInteger(value) || value < 1)) {
    throw new CliError("--episodes takes a comma-separated list of 1-based episode numbers.", {
      exitCode: EXIT_INPUT,
      errorCode: "REVIEW_EPISODES_INVALID",
      hint: "For example: --episodes 1,2,5",
      data: { expected: ["1-based episode numbers"], actual: [trimmed] },
    });
  }
  return numbers;
}

function positive(opts: Dict, key: string, fallback: number): number {
  const raw = opts[key];
  if (raw === undefined) return fallback;
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) {
    throw new CliError(`--${key.replace(/_/g, "-")} must be a positive number.`, {
      exitCode: EXIT_INPUT,
      errorCode: "REVIEW_OPTION_INVALID",
      hint: `Pass a positive number for --${key.replace(/_/g, "-")}.`,
      data: { expected: ["a positive number"], actual: [String(raw)] },
    });
  }
  return value;
}

export async function commandReview(opts: Dict): Promise<[CommandOutput, number]> {
  const llm = loadLlmConfig(strOf(opts["llm_config"]).trim() || undefined);
  const override = strOf(opts["model"]).trim();
  const profile = resolveProfile(llm, override);

  // --check answers "can this run at all" without spending a token: it is what
  // replaces `codex login status` in the caller's preflight.
  if (opts["check"]) {
    const agent = await loadAgent();
    const ok = typeof agent.reviewScript === "function";
    return [{
      text: ok
        ? `ready: ${profile.provider}/${profile.model}`
        : "the review agent bundle is present but exports no reviewScript",
      data: {
        ready: ok,
        provider: profile.provider,
        model: profile.model,
        llm_config: llm.sourcePath ?? "builtin",
      },
    }, ok ? EXIT_OK : EXIT_RUNTIME];
  }

  const scriptPath = scriptJsonPath(opts);
  if (!fs.existsSync(scriptPath)) {
    throw new CliError("Local script file not found.", {
      exitCode: EXIT_INPUT,
      errorCode: "SCRIPT_NOT_FOUND",
      hint: "Run `scriptctl checkout`, `scriptctl create`, or pass --script-path to an existing script.",
      data: { expected: ["existing local script JSON"], actual: [scriptPath] },
    });
  }
  const videos = (Array.isArray(opts["video"]) ? opts["video"] as string[] : [])
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
  const missing = videos.filter((value) => !fs.existsSync(value));
  if (missing.length > 0) {
    throw new CliError("A --video path does not exist.", {
      exitCode: EXIT_INPUT,
      errorCode: "REVIEW_SOURCE_NOT_FOUND",
      hint: "Pass --video once per episode, in episode order.",
      data: { expected: ["existing video files"], actual: missing.slice(0, 5) },
    });
  }
  const source = strOf(opts["source_path"]).trim();
  if (source && !fs.existsSync(source)) {
    throw new CliError("Video source not found.", {
      exitCode: EXIT_INPUT,
      errorCode: "REVIEW_SOURCE_NOT_FOUND",
      hint: "Pass -s to a video file or a directory of per-episode videos, or omit it for a script-only review.",
      data: { expected: ["existing video file or directory"], actual: [source] },
    });
  }

  const { reviewScript } = await loadAgent();
  const write = (event: unknown) => process.stdout.write(JSON.stringify(event) + "\n");

  try {
    await reviewScript({
      scriptPath,
      ...(source ? { source } : {}),
      ...(videos.length > 0 ? { videos } : {}),
      ...(episodeList(strOf(opts["episodes"])) ? { episodes: episodeList(strOf(opts["episodes"]))! } : {}),
      concurrency: positive(opts, "concurrency", 5),
      converge: opts["converge"] !== false,
      convergeOnly: opts["converge_only"] === true,
      apiKey: profile.apiKey,
      model: profile.model,
      mediaModel: profile.model,
      ...(profile.baseUrl ? { baseUrl: profile.baseUrl } : {}),
      maxSteps: positive(opts, "max_steps", 60),
      mediaBudget: positive(opts, "media_budget", 1),
      episodeTimeoutMs: positive(opts, "episode_timeout_ms", 8 * 60_000),
      onEvent: write,
    });
  } catch (error) {
    if (error instanceof CliError) throw error;
    throw new CliError("Review failed.", {
      exitCode: EXIT_RUNTIME,
      errorCode: "REVIEW_FAILED",
      hint: "Read the last NDJSON lines on stdout for the episode that failed.",
      data: { expected: ["a completed review run"], actual: [String((error as Error).message).slice(0, 300)] },
    });
  }

  // Every result already left as NDJSON; the envelope would only repeat it.
  return [{ text: "", data: {} }, EXIT_OK];
}
